import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {TooltipExplicitCondition} from '../../components/common/tooltips/tooltipExplicitCondition';
import {IntegrationModel} from '../../models/api/integrationModel';
import {
  IntegrationTypeModel,
  IntegrationTypesSupportedTypesEnum,
} from '../../models/api/integrationTypesModel';
import {buildSettingsAppsAppDetailsUrl} from '../../models/url/settingsUrlHelpers';
import {
  SettingsAppsAppDetailsTabsEnum,
  SettingsAppsTabsEnum,
  SettingsAppsUrl,
} from '../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {CreateUpdateIntegrationAsyncActionPayload} from '../../store/data/integrations/integrationsActions';
import {SettingsAppsDisableIntegrationDialog} from './dialogs/settingsAppsDisableIntegrationDialog';

/*
 * Props.
 */

interface SettingsAppsHeaderActionProps {
  url: SettingsAppsUrl;
  isIntegrationEnabled: boolean;
  isUserAllowedToEnableIntegrations: boolean;
  isGlobalAdmin: boolean;
  integrationType?: IntegrationTypeModel;
  integration?: IntegrationModel;

  updateIntegrationAsync: (
    integrationId: number,
    payload: CreateUpdateIntegrationAsyncActionPayload,
  ) => Promise<void>;
  createIntegrationAsync: (
    integrationType: IntegrationTypeModel,
    payload: CreateUpdateIntegrationAsyncActionPayload,
  ) => Promise<void>;
}

/*
 * Style.
 */

const StyledButtonContentWrapperDiv = styled.div`
  display: flex;
  gap: 8px;
`;

/*
 * Component.
 */

export const SettingsAppsHeaderAction: FC<SettingsAppsHeaderActionProps> = (props) => {
  const {
    url,
    integrationType,
    integration,
    isIntegrationEnabled,
    isGlobalAdmin,
    updateIntegrationAsync,
    createIntegrationAsync,
  } = props;
  const [shouldShowDisableDialog, setShouldShowDisableDialog] = useState(false);
  const {navigateToLink} = useLinkNavigation();

  // Make sure we are on the details page and have a valid integration type.
  if (!integrationType || url.tabPage !== SettingsAppsTabsEnum.DETAILS) {
    return null;
  }

  const isDisabled = computeIsDisabled(props);

  const isChannelOnly =
    integrationType.supportedTypes.length === 1 &&
    integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.CHANNEL);
  // If OAuth is connected and we do not have a plugin.
  const isOAuthOnly = Boolean(
    integrationType.oauthConnected &&
      !integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.PLUGIN),
  );

  const onLearnMoreClick = () => {
    const {publisher} = integrationType;
    if (!publisher) {
      return;
    }
    trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_LEARN_MORE, {
      integration_id: integration?.id,
      integration_type: integrationType.id,
    });
    navigateToLink(publisher.support);
  };

  const onEnableIntegration = async () => {
    // If setting up the configuration is required to enable we should open the dialog and not enable here.
    if (integrationType.isConfigurationRequiredToEnable) {
      // The enable workflow has started but requires configuration.
      trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_ENABLE_WORKFLOW, {
        integration_id: integration?.id,
        integration_type: integrationType.id,
      });
      navigateToLink(buildSettingsAppsAppDetailsUrl(url.appId, url.previousUrl, url.detailsPage, true));
      return;
    }

    // If we do not have an integration but we can enable the app we will have to create the integration.
    // We do not support passing settings here as after they enable the integration we will potentially
    // open the config.
    if (!integration) {
      await createIntegrationAsync(integrationType, {isEnabled: true});
    } else {
      await updateIntegrationAsync(integration.id, {isEnabled: true});
    }

    // We have enabled the application.
    trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_ENABLE_COMPLETED, {
      integration_id: integration?.id,
      integration_type: integrationType.id,
    });

    if (!integration || !isIntegrationEnabled) {
      navigateToLink(
        buildSettingsAppsAppDetailsUrl(
          url.appId,
          url.previousUrl,
          SettingsAppsAppDetailsTabsEnum.GETTING_STARTED,
          Boolean(integrationType.hasConfiguration && isGlobalAdmin),
        ),
      );
    }
  };

  const onDisableIntegration = async () => {
    // This should not be possible since the disable action would not be visible if we do not have an integration.
    if (!integration) {
      return;
    }
    setShouldShowDisableDialog(false);
    await updateIntegrationAsync(integration.id, {isEnabled: false});
    trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_DISABLE_COMPLETED, {
      integration_id: integration.id,
      integration_type: integrationType.id,
    });

    // If they are on any other page we will need to bring them back to the overview.
    if (url.detailsPage !== SettingsAppsAppDetailsTabsEnum.OVERVIEW) {
      navigateToLink(
        buildSettingsAppsAppDetailsUrl(url.appId, url.previousUrl, SettingsAppsAppDetailsTabsEnum.OVERVIEW),
      );
    }
  };

  const onButtonClick = () => {
    if (isIntegrationEnabled) {
      setShouldShowDisableDialog(true);
    } else {
      onEnableIntegration();
    }
  };

  // Link to help docs.
  if (integrationType.external || isChannelOnly || isOAuthOnly) {
    return (
      <Button onClick={onLearnMoreClick}>
        <StyledButtonContentWrapperDiv>
          <Icon name="externalLinkSmall" />
          {isIntegrationEnabled && <Trans id="settings-apps-header-learn-more-button">Learn more</Trans>}
          {!isIntegrationEnabled && (
            <Trans id="settings-apps-header-how-to-enable-button">How to enable</Trans>
          )}
        </StyledButtonContentWrapperDiv>
      </Button>
    );
  }
  return (
    <>
      <TooltipCoordinator render={() => renderDisabledTooltip(props)}>
        <TooltipExplicitCondition isEnabled={isDisabled} />
        <Button
          isDisabled={isDisabled}
          variant={computeButtonVariant(isIntegrationEnabled)}
          onClick={onButtonClick}
        >
          {renderButtonContent(isIntegrationEnabled)}
        </Button>
      </TooltipCoordinator>
      {shouldShowDisableDialog && (
        <SettingsAppsDisableIntegrationDialog
          onRequestClose={() => setShouldShowDisableDialog(false)}
          onDisableIntegration={onDisableIntegration}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function renderButtonContent(isIntegrationEnabled: boolean) {
  if (isIntegrationEnabled) {
    return <Trans id="settings-apps-header-disable-button">Disable app</Trans>;
  }
  return <Trans id="settings-apps-header-enable-button">Enable app</Trans>;
}

function computeButtonVariant(isIntegrationEnabled: boolean) {
  if (isIntegrationEnabled) {
    return ButtonVariantsEnum.SECONDARY_DANGER;
  }
  return ButtonVariantsEnum.PRIMARY;
}

function computeIsDisabled(props: SettingsAppsHeaderActionProps) {
  const {isGlobalAdmin, isUserAllowedToEnableIntegrations, integrationType, isIntegrationEnabled} = props;

  // Can't enable if we do not have an integration type.
  if (!integrationType) {
    return true;
  }

  // Company has preference disabled to allow non-admins to enable apps.
  if (!isUserAllowedToEnableIntegrations && !isGlobalAdmin) {
    return true;
  }

  // The integration has a required configuration but the user is not a global admin.
  if (integrationType.isConfigurationRequiredToEnable && !isGlobalAdmin) {
    return true;
  }

  // If the integration has a configuration and the user is not a global admin and it is enabled
  // only admins can disable.
  if (integrationType.hasConfiguration && !isGlobalAdmin && isIntegrationEnabled) {
    return true;
  }

  return false;
}

function renderDisabledTooltip(props: SettingsAppsHeaderActionProps) {
  const {isGlobalAdmin, isUserAllowedToEnableIntegrations, integrationType, isIntegrationEnabled} = props;

  // Company has preference disabled to allow non-admins to enable apps
  if (!isUserAllowedToEnableIntegrations && !isGlobalAdmin) {
    if (!isIntegrationEnabled) {
      return (
        <Trans id="settings-apps-header-only-admins-enable-tooltip">
          Only admins can enable apps. Contact your admin for help.
        </Trans>
      );
    }
    return (
      <Trans id="settings-apps-header-only-admins-disable-tooltip">
        Only admins can disable apps. Contact your admin for help.
      </Trans>
    );
  }

  // The integration has a configuration, is enabled and the user is not a global admin.
  if (integrationType?.hasConfiguration && !isGlobalAdmin && isIntegrationEnabled) {
    return (
      <Trans id="settings-apps-header-optional-config-disable-tooltip">
        Only admins can disable this app. Contact your admin for help.
      </Trans>
    );
  }

  // The integration has a required configuration but the user is not a global admin.
  if (integrationType?.isConfigurationRequiredToEnable && !isGlobalAdmin) {
    if (!isIntegrationEnabled) {
      return (
        <Trans id="settings-apps-header-required-config-enable-tooltip">
          Only admins can enable this app. Contact your admin for help.
        </Trans>
      );
    }
    return (
      <Trans id="settings-apps-header-required-config-disable-tooltip">
        Only admins can disable this app. Contact your admin for help.
      </Trans>
    );
  }

  return null;
}
